/**
 * SMS helpers for the console: how many parts a message costs, when a
 * message goes out, and how to say that time in plain words.
 */

// GSM 03.38 — the 7-bit alphabet every handset supports. Anything outside it
// switches the whole message to UCS-2, which cuts a part from 160 to 70.
const GSM_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
// Extension table: allowed in GSM-7 but each costs an escape plus the char.
const GSM_EXTENDED = '^{}\\[~]|€\f';

// Field names may contain spaces ({{Full Name}}), as smart broadcast sheets do.
const PLACEHOLDER = /\{\{\s*[\w.][\w. ]*?\s*\}\}/g;
/** Length assumed for a personalisation field such as {{firstName}}. */
const PLACEHOLDER_STAND_IN = 'xxxxxxxx';

export type SmsEncoding = 'GSM-7' | 'Unicode';

export interface SmsParts {
  /** Characters as the network counts them. */
  length: number;
  parts: number;
  encoding: SmsEncoding;
  /** Characters available in one part at this encoding and part count. */
  perPart: number;
  /** First character that forced Unicode, so the cost can be explained. */
  offender: string | null;
  /** True when placeholders make the final length depend on the contact. */
  estimated: boolean;
  /** Fill of each part, 0–1, for drawing a ruler. */
  fills: number[];
}

export function smsParts(raw: string | null | undefined): SmsParts {
  const source = raw ?? '';
  const estimated = PLACEHOLDER.test(source);
  PLACEHOLDER.lastIndex = 0;
  const text = source.replace(PLACEHOLDER, PLACEHOLDER_STAND_IN);

  let gsmLength = 0;
  let offender: string | null = null;
  for (const char of text) {
    if (GSM_BASIC.includes(char)) {
      gsmLength += 1;
    } else if (GSM_EXTENDED.includes(char)) {
      gsmLength += 2;
    } else {
      offender = char;
      break;
    }
  }

  const encoding: SmsEncoding = offender === null ? 'GSM-7' : 'Unicode';
  // UCS-2 counts UTF-16 code units: characters beyond the BMP take two.
  const length = encoding === 'GSM-7' ? gsmLength : text.length;
  const single = encoding === 'GSM-7' ? 160 : 70;
  const multi = encoding === 'GSM-7' ? 153 : 67;

  const parts = length === 0 ? 0 : length <= single ? 1 : Math.ceil(length / multi);
  const perPart = parts > 1 ? multi : single;
  const fills = Array.from({ length: Math.max(parts, 1) }, (_, i) =>
    Math.min(1, Math.max(0, (length - i * perPart) / perPart))
  );

  return { length, parts, encoding, perPart, offender, estimated, fills };
}

/** "the em dash", "“é”" — how to name the character that forced Unicode. */
export function describeChar(char: string): string {
  const names: Record<string, string> = {
    '—': 'an em dash',
    '–': 'an en dash',
    '’': 'a curly apostrophe',
    '‘': 'a curly apostrophe',
    '“': 'a curly quote',
    '”': 'a curly quote',
    '…': 'an ellipsis character',
    ' ': 'a non-breaking space',
  };
  return names[char] ?? `“${char}”`;
}

export interface SmsSegment {
  text: string;
  /** Set when this segment is a placeholder such as {{firstName}}. */
  field?: string;
}

/** Split copy into plain text and placeholder fields for display. */
export function smsSegments(raw: string | null | undefined): SmsSegment[] {
  const source = raw ?? '';
  const segments: SmsSegment[] = [];
  let last = 0;
  for (const match of source.matchAll(/\{\{\s*([\w.][\w. ]*?)\s*\}\}/g)) {
    const index = match.index ?? 0;
    if (index > last) segments.push({ text: source.slice(last, index) });
    segments.push({ text: match[0], field: humanizeField(match[1]) });
    last = index + match[0].length;
  }
  if (last < source.length) segments.push({ text: source.slice(last) });
  return segments;
}

/** firstName → "first name", contact.city → "city". */
function humanizeField(name: string): string {
  const leaf = name.split('.').pop() ?? name;
  return leaf
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
}

interface MessageTiming {
  messageType?: string | null;
  scheduleDate?: string | null;
  scheduleTime?: string | null;
  /** IANA zone the schedule's date and time are written in. */
  scheduleTimeZone?: string | null;
  sendAt?: string | null;
  createdAt?: string | null;
}

export function isScheduled(message: MessageTiming | null | undefined): boolean {
  return /^sched/i.test(message?.messageType ?? '');
}

/**
 * When a message goes (or went) out: its schedule for scheduled sends,
 * otherwise the send time, falling back to when it was created.
 */
export function sendMoment(message: MessageTiming | null | undefined): Date | null {
  if (!message) return null;

  if (isScheduled(message) && message.scheduleDate) {
    const scheduled = parseSchedule(message.scheduleDate, message.scheduleTime, message.scheduleTimeZone);
    if (scheduled) return scheduled;
  }

  return toDate(message.sendAt) ?? toDate(message.createdAt);
}

/**
 * A schedule's wall-clock date and time as an instant. They are read in
 * `zone` when it is set and known, otherwise in the browser's zone.
 */
function parseSchedule(date: string, time?: string | null, zone?: string | null): Date | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!day) return toDate(date);
  const clock = /^(\d{1,2}):(\d{2})/.exec(time ?? '') ?? [null, '0', '0'];
  const parts = [Number(day[1]), Number(day[2]) - 1, Number(day[3]), Number(clock[1]), Number(clock[2])] as const;
  if (zone) {
    const zoned = fromZonedTime(parts, zone);
    if (zoned) return zoned;
  }
  return new Date(parts[0], parts[1], parts[2], parts[3], parts[4]);
}

const zoneFormats = new Map<string, Intl.DateTimeFormat>();

/** Milliseconds a zone's wall clock is ahead of UTC at an instant; null for an unknown zone. */
export function zoneOffset(instant: number, zone: string): number | null {
  try {
    let format = zoneFormats.get(zone);
    if (!format) {
      format = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      zoneFormats.set(zone, format);
    }
    const parts = format.formatToParts(new Date(instant));
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return asUtc - Math.floor(instant / 1000) * 1000;
  } catch {
    return null;
  }
}

/**
 * Wall-clock parts (year, month 0–11, day, hour, minute) in an IANA zone as
 * the instant they name. Null for an unknown zone.
 */
export function fromZonedTime(
  [year, month, day, hour, minute]: readonly [number, number, number, number, number],
  zone: string
): Date | null {
  const wall = Date.UTC(year, month, day, hour, minute);
  if (Number.isNaN(wall)) return null;
  const first = zoneOffset(wall, zone);
  if (first === null) return null;
  let instant = wall - first;
  // Near a daylight-saving change the offset at the answer can differ.
  const second = zoneOffset(instant, zone);
  if (second !== null && second !== first) instant = wall - second;
  return new Date(instant);
}

export function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

const clockFormat = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const weekdayFormat = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const dayFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
});
const yearFormat = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** "14:05", "09:12": the time of day alone, 24-hour like the rest of the console. */
export function formatClock(value: Date = new Date()): string {
  return clockFormat.format(value);
}

/**
 * "Today, 14:05", "Yesterday, 09:12", "Tue 13 Oct, 09:00", "12 Oct 2025".
 * Times only appear within a fortnight either side of today, where the hour
 * still matters; older dates read as days.
 */
export function formatWhen(value: Date | string | null | undefined, now = new Date()): string {
  const date = toDate(value);
  if (!date) return '—';

  const days = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
  const clock = clockFormat.format(date);

  if (days === 0) return `Today, ${clock}`;
  if (days === -1) return `Yesterday, ${clock}`;
  if (days === 1) return `Tomorrow, ${clock}`;
  if (Math.abs(days) <= 14) return `${weekdayFormat.format(date)}, ${clock}`;
  if (date.getFullYear() === now.getFullYear()) return dayFormat.format(date);
  return yearFormat.format(date);
}

/** "in 42 min", "in 3 h 10 min", "in 2 days" — or "due now" once it passes. */
export function formatCountdown(value: Date | null, now = new Date()): string {
  if (!value) return '';
  const minutes = Math.round((value.getTime() - now.getTime()) / 60000);
  if (minutes <= 0) return 'due now';
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest ? `in ${hours} h ${rest} min` : `in ${hours} h`;
  }
  const days = Math.round(hours / 24);
  return days === 1 ? 'in 1 day' : `in ${days} days`;
}

/** 12481 → "12,481". */
export function formatCount(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-GB').format(value ?? 0);
}
